import React from "react";
import type { ComponentCategory, ComponentDefinition, SkeletonSpec } from "../../Pages/pageComponentLibrary";

/**
 * Static (non-animated) skeleton thumbnail summarising a component's shape in
 * the library picker. Deterministic slate shapes with exactly one brand accent
 * per thumb : a design-system preview, not a loading placeholder. The concrete
 * shape comes from `definition.skeleton`; when absent it is derived from the
 * component's category.
 */

const BAR = "rounded bg-slate-200";
const ACCENT = "rounded bg-[#005CA9]/20";
const DOT_ACCENT = "rounded-full bg-[#FFA401]";

/** Category → default skeleton kind when a definition sets no explicit spec. */
const CATEGORY_DEFAULT: Record<ComponentCategory, SkeletonSpec> = {
	texte: { kind: "paragraph" },
	medias: { kind: "image" },
	cartes: { kind: "cards" },
	"mise-en-page": { kind: "columns", spans: [6, 6] },
	donnees: { kind: "chart" },
	formulaires: { kind: "form" },
	accueil: { kind: "hero" },
	"pages-dediees": { kind: "heading" }
};

function Paragraph() {
	return (
		<div className="flex h-full flex-col justify-center gap-1.5">
			<div className={`h-2 w-1/3 ${ACCENT}`} />
			<div className={`h-1.5 w-full ${BAR}`} />
			<div className={`h-1.5 w-full ${BAR}`} />
			<div className={`h-1.5 w-4/5 ${BAR}`} />
		</div>
	);
}

function Heading() {
	return (
		<div className="flex h-full flex-col justify-center gap-2">
			<div className={`h-3 w-2/3 ${ACCENT}`} />
			<div className={`h-1.5 w-1/3 ${BAR}`} />
		</div>
	);
}

function Hero() {
	return (
		<div className="flex h-full flex-col gap-2">
			<div className={`flex-1 ${BAR}`} />
			<div className={`h-2 w-1/2 ${ACCENT}`} />
			<div className="flex items-center gap-1.5">
				<div className={`h-1.5 w-2/3 ${BAR}`} />
				<div className={`h-3 w-8 ${DOT_ACCENT}`} />
			</div>
		</div>
	);
}

function ImageThumb() {
	return (
		<div className="flex h-full">
			<div className={`flex-1 ${BAR} flex items-center justify-center`}>
				<div className={`h-4 w-4 ${DOT_ACCENT}`} />
			</div>
		</div>
	);
}

function Gallery({ count = 4 }: { count?: number }) {
	return (
		<div className="grid h-full grid-cols-2 gap-1.5">
			{Array.from({ length: count }).map((_, i) => (
				<div key={i} className={i === 0 ? ACCENT : BAR} />
			))}
		</div>
	);
}

function Cards({ count = 3, withImage = false }: { count?: number; withImage?: boolean }) {
	const cols = count >= 6 ? "grid-cols-3" : count === 4 ? "grid-cols-2" : "grid-cols-3";
	const shown = Math.min(count, 6);
	return (
		<div className={`grid h-full ${cols} gap-1.5`}>
			{Array.from({ length: shown }).map((_, i) => (
				<div key={i} className="flex flex-col gap-1 rounded border border-slate-100 p-1">
					{withImage && <div className={`h-1/2 ${i === 0 ? ACCENT : BAR}`} />}
					<div className={`h-1.5 w-full ${i === 0 && !withImage ? ACCENT : BAR}`} />
					<div className={`h-1 w-2/3 ${BAR}`} />
				</div>
			))}
		</div>
	);
}

function ListThumb({ rows = 3 }: { rows?: number }) {
	return (
		<div className="flex h-full flex-col justify-center gap-1.5">
			{Array.from({ length: rows }).map((_, i) => (
				<div key={i} className="flex items-center gap-1.5">
					<div className={`h-2 w-2 ${i === 0 ? DOT_ACCENT : "rounded-full bg-slate-200"}`} />
					<div className={`h-1.5 flex-1 ${BAR}`} />
				</div>
			))}
		</div>
	);
}

function Columns({ spans }: { spans: number[] }) {
	const total = spans.reduce((a, b) => a + b, 0) || 12;
	return (
		<div className="flex h-full items-stretch gap-1.5">
			{spans.map((s, i) => (
				<div
					key={i}
					className={`${i === 0 ? ACCENT : BAR}`}
					style={{ flexGrow: s, flexBasis: `${(s / total) * 100}%` }}
				/>
			))}
		</div>
	);
}

function TableThumb() {
	return (
		<div className="flex h-full flex-col gap-1">
			<div className={`h-2.5 w-full ${ACCENT}`} />
			{Array.from({ length: 3 }).map((_, i) => (
				<div key={i} className={`h-1.5 w-full ${BAR}`} />
			))}
		</div>
	);
}

function FormThumb() {
	return (
		<div className="flex h-full flex-col justify-center gap-2">
			<div className={`h-3 w-full rounded border border-slate-200 bg-slate-50`} />
			<div className={`h-3 w-full rounded border border-slate-200 bg-slate-50`} />
			<div className={`h-3 w-1/3 ${ACCENT}`} />
		</div>
	);
}

function ChartThumb() {
	const heights = [40, 70, 55, 90, 60];
	return (
		<div className="flex h-full items-end gap-1.5">
			{heights.map((h, i) => (
				<div
					key={i}
					className={`flex-1 ${i === 3 ? "rounded-t bg-[#005CA9]/30" : "rounded-t bg-slate-200"}`}
					style={{ height: `${h}%` }}
				/>
			))}
		</div>
	);
}

function CtaThumb() {
	return (
		<div className="flex h-full flex-col items-center justify-center gap-2">
			<div className={`h-2 w-2/3 ${BAR}`} />
			<div className={`h-4 w-1/2 rounded-full bg-[#005CA9]/30`} />
		</div>
	);
}

function TickerThumb() {
	return (
		<div className="flex h-full items-center">
			<div className="flex w-full items-center gap-1.5 rounded border border-slate-100 p-1.5">
				{Array.from({ length: 3 }).map((_, i) => (
					<div key={i} className={`h-2 flex-1 ${i === 1 ? ACCENT : BAR}`} />
				))}
			</div>
		</div>
	);
}

function VideoThumb() {
	return (
		<div className={`flex h-full items-center justify-center ${BAR}`}>
			<div
				className="ml-1 h-0 w-0"
				style={{
					borderTop: "7px solid transparent",
					borderBottom: "7px solid transparent",
					borderLeft: "11px solid #FFA401"
				}}
			/>
		</div>
	);
}

function QuoteThumb() {
	return (
		<div className="flex h-full items-center">
			<div className="h-3/4 w-1 rounded bg-[#FFA401]" />
			<div className="ml-2 flex flex-1 flex-col gap-1.5">
				<div className={`h-1.5 w-full ${BAR}`} />
				<div className={`h-1.5 w-3/4 ${BAR}`} />
				<div className={`h-1.5 w-1/3 ${ACCENT}`} />
			</div>
		</div>
	);
}

function renderShape(spec: SkeletonSpec): React.ReactNode {
	switch (spec.kind) {
		case "paragraph":
			return <Paragraph />;
		case "heading":
			return <Heading />;
		case "hero":
			return <Hero />;
		case "image":
			return <ImageThumb />;
		case "gallery":
			return <Gallery count={spec.count} />;
		case "cards":
			return <Cards count={spec.count} withImage={spec.withImage} />;
		case "list":
			return <ListThumb rows={spec.rows} />;
		case "columns":
			return <Columns spans={spec.spans} />;
		case "table":
			return <TableThumb />;
		case "form":
			return <FormThumb />;
		case "chart":
			return <ChartThumb />;
		case "cta":
			return <CtaThumb />;
		case "ticker":
			return <TickerThumb />;
		case "video":
			return <VideoThumb />;
		case "quote":
			return <QuoteThumb />;
		default:
			return <Paragraph />;
	}
}

const ComponentSkeletonThumb: React.FC<{ definition: ComponentDefinition; compact?: boolean }> = ({
	definition,
	compact = false
}) => {
	const spec = definition.skeleton ?? CATEGORY_DEFAULT[definition.category];
	return (
		<div
			data-skeleton-thumb={definition.type}
			className={`aspect-[16/9] w-full overflow-hidden rounded-lg border border-slate-200 bg-white ${
				compact ? "p-2" : "p-3"
			}`}
			aria-hidden="true"
		>
			{renderShape(spec)}
		</div>
	);
};

export default ComponentSkeletonThumb;
